const PGAdapter = require('./../../common/pg-adapter')
const { getOrderByClause, arrayOrStringPreparer } = require('../../tools/sql-tools')
const { RouteStatus } = require('../tools')

const {
  SHARED_ROUTE_COLUMNS,
  SHARED_VIRTUAL_COLUMNS,
  SHARED_GET_RELATED,
  SHARED_COLUMN_PREPARERS,
  arrayPreparer
} = require('./route-pg-adapter-shared')

const ROUTE_VIEW_TABLE_NAME = 'avocado.vw_route_with_program'

class RouteViewPGDataAdapter extends PGAdapter {
  constructor (pgConnection, user, logger) {
    const columnPreparers = {
      ...SHARED_COLUMN_PREPARERS,
      status: arrayOrStringPreparer,
      program_ids: arrayPreparer,
      state: (key, value) => {
        const states = Array.isArray(value) ? value : [value]
        if (states.length === 1) {
          // Single state - use simple ILIKE
          return [{
            value: states[0],
            statement: `
                EXISTS (
                  SELECT 1 FROM avocado.data_shipment shipments
                  WHERE
                    shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                    shipments.destination_id ILIKE '%:state:'||%INDEX%||':%'
                )
              `
          }]
        }
        // Multiple states - use ILIKE ANY with array of patterns
        return [{
          value: states.map(s => '%:state:' + s + ':%'),
          statement: `
              EXISTS (
                SELECT 1 FROM avocado.data_shipment shipments
                WHERE
                  shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                  shipments.destination_id ILIKE ANY(%INDEX%::TEXT[])
              )
            `
        }]
      },
      origin_id: (key, value) => [
        {
          value,
          statement: `
              EXISTS (
                SELECT 1 FROM avocado.data_shipment shipments
                WHERE 
                  shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                  shipments.origin_id = %INDEX%
              )
            `
        }
      ]
    }

    super(
      pgConnection,
      ROUTE_VIEW_TABLE_NAME,
      user,
      [...SHARED_ROUTE_COLUMNS, 'program_ids', 'origin_id', 'state'],
      undefined,
      columnPreparers,
      logger,
      {
        getRelated: SHARED_GET_RELATED,
        virtualColumns: SHARED_VIRTUAL_COLUMNS,
        securedByOrganisationId: true
      }
    )
  }

  /**
   * HACK: cancelled-route-workaround
   *
   * Cancelled routes have no shipments or program linkage in the
   * vw_route_with_program view. Until org-id based isolation is in place,
   * we must override the preparers so that:
   *  - program_ids filter is dropped (cancelled routes have no programs)
   *  - state filter falls back to a route-scoped shipment query (without program join)
   *
   * @deprecated Remove when org-id based route isolation is implemented.
   *   When that lands:
   *   1. Delete this method entirely
   *   2. Delete _getCancelledCountSeparately
   *   3. Remove the call sites in getDecoratedList and getList
   *      (replace with: const { customPreparer, excludePreparers } = { customPreparer: {}, excludePreparers: [] })
   *   4. Simplify getCountsByStatus to always include cancelled in the main query
   *
   * @param {Object} filter - filter object (will NOT be mutated; a copy is returned via outFilter)
   * @returns {{ customPreparer: Object, excludePreparers: string[], outFilter: Object }}
   */
  _getCancelledRouteOverrides (filter) {
    const status = filter.status || []
    const isCancelled = status === 'cancelled' || (Array.isArray(status) && status.includes('cancelled'))

    if (!isCancelled) {
      return { customPreparer: {}, excludePreparers: [], outFilter: filter }
    }

    const { program_ids: _dropped, ...cleanFilter } = filter

    return {
      outFilter: cleanFilter,
      excludePreparers: ['program_ids'],
      customPreparer: {
        state: (key, value) => {
          const states = Array.isArray(value) ? value : [value]
          if (states.length === 1) {
            return [{
              value: states[0],
              statement: `
                  EXISTS (
                    SELECT 1 FROM avocado.data_shipment shipments
                    WHERE
                      shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                      shipments.destination_id ILIKE '%:state:'||%INDEX%||':%'
                  )
                `
            }]
          }
          return [{
            value: states.map(s => '%:state:' + s + ':%'),
            statement: `
                EXISTS (
                  SELECT 1 FROM avocado.data_shipment shipments
                  WHERE
                    shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                    shipments.destination_id ILIKE ANY(%INDEX%::TEXT[])
                  )
                `
          }]
        }
      }
    }
  }

  /**
   * Returns a json_build_object SQL fragment for the given data_location alias.
   * Keeps the field list in one place instead of duplicating it for origin and destination.
   */
  _locationJsonBuildObject (alias) {
    return `json_build_object(
                '_id', ${alias}.fsid,
                'name', ${alias}.name_of_pharmacy
              )`
  }

  addRelatedCarrierColumn () {
    return `
      (
        SELECT row_to_json(org) FROM avocado.data_organisation org
        WHERE org.id = ${this.tableName}.carrier_id
        LIMIT 1
      ) AS carrier
    `
  }

  addRelatedShipmentsColumn () {
    return `
      COALESCE(
        (
          SELECT json_agg(DISTINCT s.*)
          FROM avocado.data_shipment s
          WHERE s.route_id = ${this.tableName}.couchdb_id
        ),
        '[]'
      ) AS shipments
    `
  }

  /**
   * Returns a LATERAL JOIN subquery that enriches shipments with location + program data.
   * Uses LATERAL instead of a correlated subquery so the planner can push down
   * the route filter and use indexes on data_shipment.route_id more effectively.
   */
  addEnrichedShipmentsLateral () {
    return `
      LEFT JOIN LATERAL (
        SELECT COALESCE(json_agg(enriched_shipment), '[]') AS shipments
        FROM (
          SELECT DISTINCT ON (s.id)
            s.*,
            ${this._locationJsonBuildObject('dl_dest')} AS destination,
            ${this._locationJsonBuildObject('dl_orig')} AS origin,
            json_build_object(
              'id', dp.fsid,
              'name', dp.name
            ) AS program
          FROM avocado.data_shipment s
          LEFT JOIN avocado.data_location dl_dest ON dl_dest.fsid = s.destination_id
          LEFT JOIN avocado.data_location dl_orig ON dl_orig.fsid = s.origin_id
          LEFT JOIN avocado.data_program dp ON dp.fsid = s.program_id
          WHERE s.route_id = ${this.tableName}.couchdb_id
        ) enriched_shipment
      ) enriched_shipments ON true
    `
  }

  async getList ({
    ordering = this.idColumn,
    filter = {},
    limit = 50,
    offset = 0,
    tableExpression = null,
    useTableAsExpression = false,
    search = null,
    skipCount = false,
    skipData = false,
    getRelated
  } = {}) {
    // HACK: cancelled-route-workaround — remove when org-id isolation lands
    const { customPreparer, excludePreparers, outFilter } = this._getCancelledRouteOverrides(filter)
    const {whereText, whereValues} = this.getWhereCondition(outFilter, search, customPreparer, excludePreparers)
    const resultList = {}

    if (!skipData) {
      const sortText = getOrderByClause(
        ordering,
        this.columns.concat(this.virtualColumns),
        (tableExpression && useTableAsExpression ? this.tableName : undefined)
      )
      /*
      Postgresql usage reminder ():
      > ASC order is the default. Ascending order puts smaller values first,
      where “smaller” is defined in terms of the < operator.
      - https://www.postgresql.org/docs/13/queries-order.html
    */
      const values = whereValues.concat([limit, offset])

      const fromTable = tableExpression || this.tableName

      const text = `
        SELECT
          ${fromTable}.*,
          ${this.addRelatedCarrierColumn()},
          enriched_shipments.shipments
        FROM ${fromTable}
        ${this.addEnrichedShipmentsLateral()}
        ${whereText}
        ${sortText}
        LIMIT $${values.length - 1} OFFSET $${values.length};`

      let { rows: results } = await this.pgConnection.query({ text, values })

      resultList.results = results
    }

    if (!skipCount) { // Sometimes we don't need it so we may skip it for performance reasons
      resultList.count = await this.getCount(undefined, whereText, whereValues, tableExpression, useTableAsExpression)
    }

    return resultList
  }

  /**
   * Get counts for all statuses in a single query using conditional aggregation.
   * Replaces the previous N+1 query pattern (one COUNT per status + one for total).
   *
   * @param {Object} filter - The base filter (without status) from _createRouteFilter
   * @param {string[]} statusesToCheck - The statuses the user is allowed to see
   * @returns {Object} e.g. { total: 5, provisional: 2, approved: 1, ... }
   */
  async getCountsByStatus (filter, statusesToCheck) {
    // Separate status from filter — we count all requested statuses in one query
    const { status: _ignoredStatus, ...filterWithoutStatus } = filter

    // Cancelled routes have no program linkage in the view, so we must exclude
    // program_ids from the filter when counting them. We handle cancelled separately
    // only when program_ids is present in the filter.
    const hasProgramFilter = !!filterWithoutStatus.program_ids
    const includesCancelled = statusesToCheck.includes(RouteStatus.CANCELLED)
    const nonCancelledStatuses = statusesToCheck.filter(s => s !== RouteStatus.CANCELLED)

    // Build main query for non-cancelled statuses (or all if no program filter)
    const statusesForMainQuery = hasProgramFilter && includesCancelled
      ? nonCancelledStatuses
      : statusesToCheck

    const { whereText, whereValues } = this.getWhereCondition(filterWithoutStatus)

    // Build COUNT(CASE WHEN ...) columns dynamically from the statuses
    const caseColumns = statusesForMainQuery.map((status, i) => {
      const paramIndex = whereValues.length + i + 1
      return `COUNT(CASE WHEN "status" = $${paramIndex} THEN 1 END) AS "${status}"`
    })

    const mainQueryText = `
      SELECT
        ${caseColumns.join(',\n        ')}
      FROM ${this.tableName}
      ${whereText}
    `
    const mainQueryValues = [...whereValues, ...statusesForMainQuery]

    const { rows: [mainRow] } = await this.pgConnection.query({
      text: mainQueryText,
      values: mainQueryValues
    })

    // Convert string counts to numbers and derive total as their sum
    // so it only reflects statuses the user is allowed to see
    const result = {}
    let total = 0
    for (const status of statusesForMainQuery) {
      const count = Number(mainRow[status])
      result[status] = count
      total += count
    }
    result.total = total

    // HACK: cancelled-route-workaround — remove when org-id isolation lands
    await this._getCancelledCountSeparately(result, filterWithoutStatus, statusesToCheck)

    return result
  }

  /**
   * HACK: cancelled-route-workaround
   *
   * Counts cancelled routes separately when program_ids is in the filter,
   * because cancelled routes have no program linkage in the view.
   *
   * @deprecated Remove when org-id based route isolation is implemented.
   *   Delete this method and its call site in getCountsByStatus.
   */
  async _getCancelledCountSeparately (result, filterWithoutStatus, statusesToCheck) {
    const hasProgramFilter = !!filterWithoutStatus.program_ids
    const includesCancelled = statusesToCheck.includes(RouteStatus.CANCELLED)

    if (!hasProgramFilter || !includesCancelled) {
      return
    }

    const { program_ids: _ignored, ...filterWithoutProgram } = filterWithoutStatus
    const cancelledFilter = { ...filterWithoutProgram, status: RouteStatus.CANCELLED }
    const { count } = await this.getList({
      filter: cancelledFilter,
      skipCount: false,
      skipData: true
    })
    result[RouteStatus.CANCELLED] = count
    result.total += count
  }
}

module.exports = RouteViewPGDataAdapter
